module Meevo.Plugins.SelfCheckInKiosk {

    import Client = Meevo.Model.Client;
    import Phone = Meevo.Model.Phone;
    import ISelfCheckInKioskSettingsModel = Meevo.Plugins.SelfCheckInKioskSettings.ISelfCheckInKioskSettingsModel;

    export interface ISelfCheckinClient extends Client {
        primaryPhone: Phone;
    }

    export interface IBookableDataRow {
        appointmentService: ISelfCheckinAppointment;
        enhancements: (IAddOnService & { selected?: boolean; disabled?: boolean; })[];
        numberRequiredAddOns: number;
        hasRequiredAddOns: boolean;
        hasPreviouslyPurchased?: boolean;
    }

    export interface IDataToCheckInEntity {
        appointmentServicesToCheckIn: IBookableDataRow[];
        additionalAppointmentService: ISelfCheckinAppointment[];
        readonly validToCheckIn: boolean;
        readonly appointmentId: string;
        readonly allServiceIdsToCheckIn: string[];
        readonly appointmentCheckInData: ICheckInRequestBody;
        client: ISelfCheckinClient;

        setAppointments(appointments: ISelfCheckinAppointment[], locationTime: string | Date, lateCheckInMin: number): void;
    }

    export class DataToCheckInEntity implements IDataToCheckInEntity {
        appointmentServicesToCheckIn: IBookableDataRow[];
        additionalAppointmentService: ISelfCheckinAppointment[];

        get validToCheckIn(): boolean {
            return this.appointmentServicesToCheckIn.length > 0;
        }

        get appointmentId(): string {
            if (this.appointmentServicesToCheckIn.length === 0) return '';

            return this.appointmentServicesToCheckIn[0].appointmentService.appointmentId;
        }

        get appointmentCheckInData(): ICheckInRequestBody {
            return {
                suppressWarnings: true,
                appointmentId: this.appointmentServicesToCheckIn[0].appointmentService.appointmentId.toString(),
                appointmentServices: this.appointmentServicesToCheckIn.map((serviceRow) => {
                    return {
                        id: serviceRow.appointmentService.id.toString(),
                        startDateTime: serviceRow.appointmentService.startDateTime,
                        serviceAddOns: serviceRow.enhancements
                            .filter((enhancement) => enhancement.selected)
                            .map((enhancement) => {
                                return {
                                    appointmentServiceId: serviceRow.appointmentService.id.toString(),
                                    addOnServiceId: enhancement.serviceId.toString()
                                };
                            })
                    };
                })
            };
        }

        get allServiceIdsToCheckIn(): string[] {
            return [
                ...this.appointmentServicesToCheckIn.map((row) => row.appointmentService.serviceId.toString()),
                ...this.additionalAppointmentService.map((service) => service.serviceId.toString())
            ];
        }

        constructor(public client: ISelfCheckinClient, private kioskSettings: ISelfCheckInKioskSettingsModel) {
            this.appointmentServicesToCheckIn = [];
            this.additionalAppointmentService = [];

            client.primaryPhone = client.phoneNumbers.filter((phoneNumber) => phoneNumber.isPrimary)[0];
        }

        /**
         * Method returns true if NOW is allowed time to check in
         * @param appointmentDateTime - appointment start time
         * @param locationTime - current location time (it could be different to the local time)
         * @param lateCheckInMin - > 0 if allow client to checkin set to true in bus settings
         */
        private allowedTimeToCheckIn(appointmentDateTime: Date, locationTime: string | Date, lateCheckInMin: number): boolean {
            const now = moment(locationTime).toDate();

            return now < moment(appointmentDateTime).add(lateCheckInMin, 'minutes').toDate() // allow client to checkin late up to lateCheckInMin
                && now > moment(appointmentDateTime)
                    .add(parseInt(this.kioskSettings.clientCheckinThreshold, 10) * -1, 'minutes')
                    .toDate(); // allow client to checkin before
        }

        setAppointments(appointments: ISelfCheckinAppointment[], locationTime: string | Date, lateCheckInMin: number) {
            if (appointments.length === 0) return;

            if (this.allowedTimeToCheckIn(new Date(appointments[0].startDateTime), locationTime, lateCheckInMin)) {
                if (!this.kioskSettings.checkInAllServices) {
                    // if Check In all services setting is not selected
                    this.appointmentServicesToCheckIn.push({
                        appointmentService: appointments[0],
                        enhancements: []
                    } as IBookableDataRow);
                    this.additionalAppointmentService.push(...appointments.slice(1));
                } else {
                    // if Check In all services setting is selected
                    this.appointmentServicesToCheckIn.push(...appointments.map((appointment) => ({
                        appointmentService: appointment,
                        enhancements: []
                    } as IBookableDataRow)));
                }
            }
        }

    }
}
